import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import invariant from "tiny-invariant";
import { TranscriptSegment } from "~/components/AssetModal/shared/components/TranscriptPanel/components/TranscriptSegmentList/components/TranscriptSegment/TranscriptSegment";
const _TranscriptSegmentMeasure = /*#__PURE__*/ forwardRef((param, forwardedRef)=>{
    let { onResize } = param;
    const containerRef = useRef(null);
    const containerWidthRef = useRef(0);
    const letterWidthRef = useRef(0);
    const wordHeightRef = useRef(0);
    const wordHorizontalPaddingRef = useRef(0);
    const segmentHeightWithoutWordsRef = useRef(0);
    const wordContainerTopOffset = useRef(0);
    const getSegmentHeight = useCallback((segment)=>{
        if (containerWidthRef.current === 0) {
            return {
                totalHeight: 0,
                lineHeight: 0,
                linesPositions: [],
                segmentHeaderHeight: 0
            };
        }
        let lineWidth = 0;
        const segmentItems = segment.items;
        // store positions of each line to calculate offset needed to scroll to current word
        const linesPositions = [];
        let newLineStartTime = segmentItems[0].startTimeMs;
        let newLineEndTime = segmentItems[0].endTimeMs;
        const wordsHeight = segmentItems.reduce((totalHeight, item, currentIndex)=>{
            const nextItem = segmentItems[currentIndex + 1];
            if (item.type === "punctuation") {
                if (currentIndex === segmentItems.length - 1) {
                    linesPositions.push({
                        startTimeMs: newLineStartTime,
                        endTimeMs: item.endTimeMs,
                        offset: totalHeight
                    });
                    newLineStartTime = item.startTimeMs;
                    return totalHeight + wordHeightRef.current;
                }
                return totalHeight;
            }
            const textToCount = (nextItem === null || nextItem === void 0 ? void 0 : nextItem.type) === "punctuation" ? item.text + nextItem.text : item.text;
            const wordWidth = textToCount.length * letterWidthRef.current + wordHorizontalPaddingRef.current;
            if (wordWidth > containerWidthRef.current) {
                const lines = Math.ceil(wordWidth / containerWidthRef.current);
                const totalHeightForWord = wordHeightRef.current * lines;
                linesPositions.push({
                    startTimeMs: item.startTimeMs,
                    endTimeMs: item.endTimeMs,
                    offset: totalHeight
                });
                newLineStartTime = item.startTimeMs;
                return totalHeight + totalHeightForWord;
            } else {
                lineWidth += wordWidth;
                if (lineWidth > containerWidthRef.current) {
                    lineWidth = wordWidth;
                    linesPositions.push({
                        startTimeMs: newLineStartTime,
                        endTimeMs: newLineEndTime,
                        offset: totalHeight
                    });
                    newLineStartTime = item.startTimeMs;
                    if (currentIndex === segmentItems.length - 1) {
                        newLineEndTime = item.endTimeMs;
                        return totalHeight + 2 * wordHeightRef.current;
                    }
                    return totalHeight + wordHeightRef.current;
                } else if (currentIndex === segmentItems.length - 1) {
                    linesPositions.push({
                        startTimeMs: newLineStartTime,
                        endTimeMs: item.endTimeMs,
                        offset: totalHeight
                    });
                    return totalHeight + wordHeightRef.current;
                }
            }
            newLineEndTime = item.endTimeMs;
            return totalHeight;
        }, 0);
        const totalHeight = segmentHeightWithoutWordsRef.current + wordsHeight;
        return {
            totalHeight,
            linesPositions,
            lineHeight: wordHeightRef.current,
            segmentHeaderHeight: wordContainerTopOffset.current
        };
    }, []);
    useImperativeHandle(forwardedRef, ()=>({
            getSegmentHeight
        }));
    useEffect(()=>{
        if (!containerRef.current) {
            return;
        }
        const segmentDiv = containerRef.current.getElementsByClassName("group/segment")[0];
        invariant(segmentDiv, "TranscriptSegmentMeasure - Segment div not found");
        const headerDiv = segmentDiv.getElementsByTagName("div")[0];
        invariant(headerDiv, "TranscriptSegmentMeasure - Header div not found");
        const wordButton = containerRef.current.getElementsByClassName("group/transcript-word")[0];
        invariant(wordButton, "TranscriptSegmentMeasure - Word button not found");
        const wordDiv = wordButton.getElementsByTagName("div")[0];
        invariant(wordDiv, "TranscriptSegmentMeasure - Word div not found");
        const divRect = wordDiv.getBoundingClientRect();
        containerWidthRef.current = headerDiv.offsetWidth - 15; // 15 is scrollbar width;
        wordContainerTopOffset.current = wordButton.offsetTop;
        letterWidthRef.current = parseFloat(divRect.width.toFixed(1)); // we need rect, because clientWidth is rounded, and this value is 8.41;
        wordHeightRef.current = wordButton.clientHeight;
        wordHorizontalPaddingRef.current = wordButton.clientWidth - wordDiv.clientWidth;
        segmentHeightWithoutWordsRef.current = containerRef.current.clientHeight - wordHeightRef.current;
        const wordResizeObserver = new ResizeObserver((entries)=>{
            letterWidthRef.current = entries[0].contentRect.width; // we need rect, because clientWidth is rounded, and this value is 8.41;
            onResize();
        });
        wordResizeObserver.observe(wordDiv);
        return ()=>{
            wordResizeObserver.disconnect();
        };
    }, [
        onResize
    ]);
    return /*#__PURE__*/ _jsx("div", {
        ref: containerRef,
        children: /*#__PURE__*/ _jsx(TranscriptSegment, {
            onSegmentPersistedOnScreen: undefined,
            segment: {
                endTimeMs: -1,
                startTimeMs: -1,
                id: "0",
                items: [
                    {
                        startTimeMs: -1,
                        endTimeMs: -1,
                        text: "0",
                        type: "pronunciation"
                    }
                ]
            }
        })
    });
});
_TranscriptSegmentMeasure.displayName = "TranscriptSegmentMeasure";
export const TranscriptSegmentMeasure = /*#__PURE__*/ memo(_TranscriptSegmentMeasure);
TranscriptSegmentMeasure.displayName = "TranscriptSegmentMeasure";
